import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocalSettingsService } from './local-settings/local-settings.service';

const selLng = 'SELECTED_LANGUAGE';

@Injectable({
  providedIn: 'root'
})
export class LanguageSupportService {
  localLanguage: any;
  private readonly languageInitializations = new Map<string, Promise<void>>();

  constructor(private translate: TranslateService, public localSettingsSrv: LocalSettingsService) { }

  getDefaultLanguage() {
    const language = this.resolveDefaultLanguage();
    this.translate.setDefaultLang(language);
    if (this.translate.currentLang !== language) {
      this.translate.use(language);
    }
    return language;
  }

  initialize(): Promise<void> {
    const language = this.resolveDefaultLanguage();
    const existingInitialization = this.languageInitializations.get(language);
    if (existingInitialization) {
      return existingInitialization;
    }

    this.translate.setDefaultLang(language);
    const initialization = this.translate.reloadLang(language).toPromise().then(result => {
      this.translate.setTranslation(language, result, true);
      if (this.translate.currentLang === language) {
        return undefined;
      }
      return this.translate.use(language).toPromise().then(() => undefined);
    }).catch(error => {
      this.languageInitializations.delete(language);
      throw error;
    });
    this.languageInitializations.set(language, initialization);
    return initialization.then(() => {
      if (this.languageInitializations.get(language) === initialization) {
        this.languageInitializations.delete(language);
      }
    });
  }

  setLanguage(setLang) {
    this.translate.use(setLang);
    this.localSettingsSrv.setLanguagePreference('ln_pref', setLang);
    this.localLanguage = this.localSettingsSrv.setLanguagePreference('ln_pref', setLang);
  }

  private resolveDefaultLanguage(): string {
    const preferredLanguage = this.localSettingsSrv.getLanguagePreference('ln_pref');
    if (preferredLanguage) {
      return preferredLanguage;
    }

    const browserLanguage = this.translate.getBrowserLang();
    const supportedLanguages = ['en', 'pt', 'it', 'fr', 'de', 'zh', 'pl', 'nl', 'tr', 'ko', 'vi', 'th', 'da', 'sv'];
    if (supportedLanguages.includes(browserLanguage)) {
      return browserLanguage;
    }
    return browserLanguage === 'es' ? 'sp' : 'en';
  }
}
